// Useful docs for auth0 are available here https://auth0.github.io/auth0-spa-js/
// They're a little hard to find via Google.

import {
  createAuth0Client,
  Auth0Client,
  User,
  GenericError,
  AuthenticationError,
  RedirectLoginOptions,
  GetTokenSilentlyOptions,
} from "@auth0/auth0-spa-js";
import store from "./store";
import * as _ from "lodash";
import { addBreadcrumb, captureException } from "./sentry";
import {
  BASE_URL,
  MFA_REQUIRES_REAUTH_INTERVAL,
  PENDING_CUSTOMER_SELECTION_KEY,
} from "./constants";
import { getDomainFromEmail } from "./util/string";
import orderHistoryStore from "./sub-stores/order-history";
import { useKybFormStore } from "./sub-stores/kybForm";
import jwtDecode from "jwt-decode";
import { datadogRum } from "@datadog/browser-rum";

// Where to send user after logout
const LOGOUT_URL = import.meta.env.VITE_AUTH0_LOGOUT_URL ?? location.origin;

const MFA_AUTH_TIME_CLAIM_KEY = import.meta.env.VITE_MFA_AUTH_TIME_CLAIM_KEY;

// Public domains for social providers supported.
// Use to check if user email domain is in a public domain we support.
// If more social connections added, add its entry here along with its public emails list.
const SUPPORTED_SOCIAL_PROVIDERS_PUBLIC_DOMAINS = {
  google: ["gmail.com", "googlemail.com"],
};

// Start of custom error message we've added by Auth0 Flow function
const ERROR_EMAIL_UNVERIFIED = "email-unverified:";

export class Auth0Error extends Error {
  constructor(message: string) {
    super(message);
    this.name = "Auth0Error";
  }
}

export class Auth0LoginError extends Auth0Error {
  constructor(message: string) {
    super(message);
    this.name = "Auth0LoginError";
  }
}

export class Auth0EmailVerificationRequiredError extends Auth0LoginError {
  emailAddress: string;

  constructor(message: string, emailAddress: string) {
    super(message);
    this.name = "Auth0EmailVerificationRequiredError";
    this.emailAddress = emailAddress;
  }
}

export class Auth0UserBlockedError extends Auth0LoginError {
  constructor(message: string) {
    super(message);
    this.name = "Auth0UserBlockedError";
  }
}

export class Auth0ExpirationTimeClaimError extends Auth0LoginError {
  constructor(message: string) {
    super(message);
    this.name = "Auth0ExpirationTimeClaimError";
  }
}

export class Auth0AccessDeniedError extends Auth0LoginError {
  constructor(message: string) {
    super(message);
    this.name = "Auth0AccessDeniedError";
  }
}

// Thrown when state parameter stored by auth0 in localStorage at login, cannot be validated at callback
// after response from the server is returned.
// Normally happens when the callback url is hit manually by user either directly opened in browser,
// through back button, or refreshing on the callback page etc.
export class Auth0InvalidStateError extends Auth0Error {
  constructor(message: string) {
    super(message);
    this.name = "Auth0InvalidStateError";
  }
}

// Thrown when a request is made for a token but either there is no current
// session or the session has expired.
export class Auth0LoginRequired extends Auth0Error {
  constructor(message: string) {
    super(message);
    this.name = "Auth0LoginRequired";
  }
}

let instance: Auth0Client;

const clientPromise = createAuth0Client({
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  authorizationParams: {
    audience: import.meta.env.VITE_AUTH0_AUDIENCE, // You don't get a JWT without this.
    organization: import.meta.env.VITE_AUTH0_ORGANIZATION,
    redirect_uri: `${BASE_URL}/auth/callback`,
  },
  cacheLocation: "localstorage",
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
})
  .then((auth0) => {
    instance = auth0;
  })
  .catch((e) => {
    console.log(e);
  });

// Get client, waiting until it's available
export async function getInstance(): Promise<Auth0Client> {
  await clientPromise;
  return instance;
}

// Redirect user to auth0 login
export async function doLogin(loginOptions: RedirectLoginOptions = {}) {
  const auth0 = await getInstance();
  await auth0.loginWithRedirect(loginOptions);
}

/**
 * Handle AuthenticationError cases with specific error types
 */
function handleAuthenticationError(
  error: AuthenticationError
): Promise<void> | never {
  if (_.startsWith(error.error_description, ERROR_EMAIL_UNVERIFIED)) {
    const emailAddress = _.split(error.error_description, ":", 2)[1];
    throw new Auth0EmailVerificationRequiredError(
      error.error_description,
      emailAddress
    );
  }

  if (error.error_description === "user is blocked") {
    throw new Auth0UserBlockedError(error.error_description);
  }

  // Block users that do not belong to an Auth0 organization
  if (isInvalidOrganizationError(error)) {
    throw new Auth0AccessDeniedError("Access denied");
  }

  // Handles duplicate signup attempts
  if (error.error === "access_denied") {
    throw new Auth0AccessDeniedError(error.error_description);
  }

  // Unknown error, capture it
  captureException(error);
  throw new Auth0LoginError(error.error_description);
}

/**
 * Handle non-AuthenticationError cases (generic errors)
 */
function handleGenericError(error: unknown): never {
  const errorMessage = (error as Error)?.message || "";

  // This (exp) claim error can occur if user's local machine time is out of sync with the actual time
  if (
    errorMessage.includes("Expiration Time (exp) claim error in the ID token")
  ) {
    throw new Auth0ExpirationTimeClaimError(errorMessage);
  }

  if (errorMessage === "Invalid state") {
    throw new Auth0InvalidStateError(errorMessage);
  }

  // Not a handled error type, capture it
  captureException(error);
  throw new Auth0LoginError(`${error}`);
}

// Handle the auth0 login callback, throws an Auth0LoginError if there's an error
export async function handleLoginCallback(): Promise<void> {
  const auth0 = await getInstance();
  let redirectResult;

  try {
    redirectResult = await auth0.handleRedirectCallback();
  } catch (error) {
    addBreadcrumb({ message: "Unable to handle authentication callback" });

    if (error instanceof AuthenticationError) {
      const result = handleAuthenticationError(error);
      if (result) await result;
      return;
    } else {
      handleGenericError(error);
    }
  }
}

const pendingSelection = window?.sessionStorage?.getItem(
  PENDING_CUSTOMER_SELECTION_KEY
);
if (pendingSelection) {
  try {
    const selectionData = JSON.parse(pendingSelection);
    // Set minimal customer data in store immediately so other components don't fail
    store.setCustomerIdAndName(
      selectionData.customerId,
      selectionData.customerName
    );
  } catch (error) {
    console.error("Failed to parse pending selection:", error);
  }
}

// Restores app state from auth0 on initial load, returns true if user is authenticated
export async function restoreAuthState(): Promise<boolean> {
  console.debug("Restoring auth state");
  const auth0 = await getInstance();
  const loggedIn = await auth0.isAuthenticated();
  if (loggedIn) {
    const user = await auth0.getUser();
    console.debug("User is authenticated: " + user?.sub);
    if (!user) {
      throw new Error("Auth0 did not return a valid user");
    }
    return true;
  } else {
    // User is unauthenticated, caller will likely want to send to a login page
    return false;
  }
}

export async function isUserAuthenticated() {
  const auth0 = await getInstance();
  return await auth0.isAuthenticated();
}

// Handle logout, clears anything that needs clearing and sends user to auth0 logout page
export async function doLogout() {
  const auth0 = await getInstance();
  orderHistoryStore.clearStorage();
  useKybFormStore().clear();
  store.clearCustomer();
  store.clearIsStaff();
  auth0.logout({ logoutParams: { returnTo: LOGOUT_URL || BASE_URL } });
}

// Get a JWT token, will force a logout if auth is no longer valid
export async function getJwtToken(
  options?: GetTokenSilentlyOptions
): Promise<string> {
  const auth0 = await getInstance();
  try {
    const token = await auth0.getTokenSilently(options);
    const decodedToken = store.decodeJwtToken(token);
    store.setStatePermissionsFromAuth0JwtToken(decodedToken);
    store.setCustomerIdFromJwtToken(decodedToken);
    return token;
  } catch (error) {
    if (error instanceof GenericError) {
      if (
        error.error === "login_required" ||
        error.error === "mfa_required" ||
        error.error === "missing_refresh_token"
      ) {
        await doLogin();
        // Throw exception so the request to our backend doesn't happen and we don't get a 401
        throw new Auth0LoginRequired("Login required");
      }
    }
    // TODO: Monitor what exceptions are thrown here and handle them appropriately, then reduce the logging
    captureException(error);
    throw new Auth0Error(`${error}`);
  }
}

// Used to match the OpenAPI.TOKEN type
export async function jwtTokenOpenApiResolver(
  _options?: unknown
): Promise<string> {
  const token = await getJwtToken();
  if (_options) {
    const opts = _options as {
      method?: string;
      url?: string;
    };
    const orgIdInToken = store.decodeJwtToken(token)?.org_id;
    datadogRum.addAction("FetchingJWTForRequest", {
      method: opts.method,
      url: opts.url,
      org_id: orgIdInToken,
      isOrgAuthUsed: !!orgIdInToken,
    });
  }
  return token;
}

export async function checkIfUserAuthenticated(): Promise<boolean> {
  const auth0 = await getInstance();
  return await auth0.isAuthenticated();
}

export async function getUser(): Promise<User | undefined> {
  const instance = getInstance();
  return (await instance).getUser();
}

/**
 * NOTE: This assumes database or username/password login using public email restriction is handled at auth0 level using actions/hooks/rules etc.
 * This checks if user email domain is in public email domains of the social auth providers we support.
 */
export async function userLoggedInWithPublicEmail() {
  const user = await getUser();
  const domain = getDomainFromEmail(user?.email || "");
  const notAllowedDomains = Object.values(
    SUPPORTED_SOCIAL_PROVIDERS_PUBLIC_DOMAINS
  ).reduce<string[]>((prev, curr) => {
    return [...prev, ...curr];
  }, []);
  if (notAllowedDomains.includes(domain)) {
    return true;
  }
  return false;
}

export async function isMfaOutdated() {
  const token = await getJwtToken({
    authorizationParams: {
      cacheMode: "off",
    },
  });
  const decodedToken: any = jwtDecode(token);
  if (!decodedToken[MFA_AUTH_TIME_CLAIM_KEY] || null) {
    return true;
  }
  const mfaAuthTime = decodedToken[MFA_AUTH_TIME_CLAIM_KEY];
  const authTime = new Date(mfaAuthTime);
  const now = new Date();
  const reauthThreshold = new Date(
    now.getTime() - MFA_REQUIRES_REAUTH_INTERVAL * 60 * 1000
  );

  if (authTime < reauthThreshold) {
    return true;
  }
  return false;
}

/**
 * Checks if the given AuthenticationError indicates an invalid organization scenario.
 * One is when the organization is invalid, another is when user is not part of the organization.
 * @param error - The AuthenticationError to check
 * @returns True if the error indicates an invalid organization or
 * when a user is not part of an organization, false otherwise
 */
function isInvalidOrganizationError(error: AuthenticationError): boolean {
  const code = (error.error || "").trim().toLowerCase();
  const description = (error.error_description || "").toLowerCase();

  /** Case 1 — Auth0 returns:
    error: invalid_request
    error_description: "Organization does not exist"
  */
  const isInvalidOrganization =
    code === "invalid_request" && /organization/i.test(description);

  /** Case 2 — Auth0 returns:
    error: access_denied
    error_description: "user google-oauth2|xxxxx is not part of the org_abc organization"
  */
  const isNotPartOfOrganization =
    code === "access_denied" && description.includes("is not part of the org_");

  return isInvalidOrganization || isNotPartOfOrganization;
}

/**
 * Clears all authentication-related state from stores.
 * Used when initiating a new authentication flow or logging out.
 * Clears: order history, customer data, staff status, and user permissions.
 */
export function clearState() {
  orderHistoryStore.clearStorage();
  useKybFormStore().clear();
  store.clearCustomer();
  store.clearIsStaff();
  store.clearUserPermissions();
}

/**
 * Stores customer context in sessionStorage before clearing state and redirecting to Auth0
 * with organization parameter. If organization auth fails, handleLoginCallback will fallback
 * to standard auth and restore the customer selection from sessionStorage.
 *
 * @param customerId - The customer ID to use as organization identifier
 */
export async function initiateOrganizationLogin(
  customerId: string
): Promise<void> {
  try {
    sessionStorage.setItem(
      PENDING_CUSTOMER_SELECTION_KEY,
      JSON.stringify({
        customerId: customerId,
        customerName: store.state.customerName || "",
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.error(
      "Failed to store customer selection - customer switching will not work:",
      error
    );
    throw error;
  }

  clearState();
  const organizationName = customerId.toLowerCase();
  const auth0 = await getInstance();

  // Always try organization auth first
  await auth0.loginWithRedirect({
    authorizationParams: {
      organization: organizationName,
    },
  });
}

/**
 * Retrieves and removes pending customer selection from sessionStorage.
 * Returns the customer data if valid and not stale, null otherwise.
 * Handles all error cases and cleanup automatically.
 */
export function popPendingCustomer(): {
  customerId: string;
  customerName?: string;
} | null {
  let pendingSelection: string | null = null;

  try {
    pendingSelection =
      typeof window !== "undefined"
        ? window.sessionStorage?.getItem(PENDING_CUSTOMER_SELECTION_KEY) ?? null
        : null;
  } catch (error) {
    console.warn(
      "SessionStorage unavailable - customer context may be lost:",
      error
    );
    pendingSelection = null;
  }

  if (!pendingSelection) {
    return null;
  }

  try {
    const selectionData = JSON.parse(pendingSelection) as {
      customerId?: unknown;
      customerName?: unknown;
      timestamp?: unknown;
    };

    const customerId =
      typeof selectionData.customerId === "string"
        ? selectionData.customerId
        : null;
    const customerName =
      typeof selectionData.customerName === "string"
        ? selectionData.customerName
        : undefined;

    const ts = selectionData.timestamp;
    // 10 minutes - balances user experience while preventing stale data
    const MAX_AGE_MS = 10 * 60 * 1000;

    // Check if the pending selection is stale
    if (
      !customerId ||
      (typeof ts === "number" && Date.now() - ts > MAX_AGE_MS)
    ) {
      return null;
    }

    return { customerId, customerName };
  } catch (error) {
    console.error("Failed to parse pending customer selection:", error);
    return null;
  } finally {
    // Always clean up sessionStorage
    try {
      window.sessionStorage?.removeItem(PENDING_CUSTOMER_SELECTION_KEY);
    } catch {
      // Ignore cleanup errors
    }
  }
}
